import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Lock, Mail, LogIn, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function AdminLoginPage() {
    const { login, user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const from = location.state?.from || "/admin";

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);

    React.useEffect(() => {
        if (user) navigate(from, { replace: true });
    }, [user, navigate, from]);

    const onSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await login(email, password);
            toast.success("Welcome back, Riley.");
            navigate(from, { replace: true });
        } catch (err) {
            toast.error(
                err?.response?.data?.detail || "Login failed — check your credentials.",
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-grain">
            <div className="min-h-screen relative overflow-hidden">
                <div className="absolute inset-0 hero-wash pointer-events-none" />
                <div className="relative mx-auto max-w-md px-4 sm:px-6 lg:px-8 py-16">
                    <Link
                        to="/"
                        className="font-display text-2xl text-moss inline-block"
                    >
                        Choice Ayurveda
                    </Link>
                    <p className="mt-1 text-xs text-foreground/55">Practitioner admin</p>

                    <div className="mt-8 rounded-3xl border border-border/80 bg-white/95 p-7 shadow-[0_18px_50px_rgba(30,35,32,0.10)]">
                        <h1 className="font-display text-2xl text-moss">Welcome back</h1>
                        <p className="mt-1 text-sm text-foreground/70">
                            Sign in to review new client intakes.
                        </p>
                        <form onSubmit={onSubmit} className="mt-6 space-y-4">
                            <label className="block">
                                <span className="block text-sm font-medium text-foreground/85">
                                    Email
                                </span>
                                <div className="mt-1.5 relative">
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground/50" />
                                    <input
                                        type="email"
                                        value={email}
                                        required
                                        autoFocus
                                        data-testid="admin-email"
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="w-full rounded-xl bg-white border border-border/80 pl-9 pr-3 py-2.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage focus-visible:ring-offset-2 focus-visible:ring-offset-cream"
                                    />
                                </div>
                            </label>
                            <label className="block">
                                <span className="block text-sm font-medium text-foreground/85">
                                    Password
                                </span>
                                <div className="mt-1.5 relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground/50" />
                                    <input
                                        type="password"
                                        value={password}
                                        required
                                        data-testid="admin-password"
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="w-full rounded-xl bg-white border border-border/80 pl-9 pr-3 py-2.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage focus-visible:ring-offset-2 focus-visible:ring-offset-cream"
                                    />
                                </div>
                            </label>
                            <button
                                type="submit"
                                disabled={submitting}
                                data-testid="admin-login-submit"
                                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-moss text-cream px-5 py-3 text-sm font-medium hover:bg-moss/90 disabled:opacity-60"
                            >
                                {submitting ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <LogIn className="h-4 w-4" />
                                )}
                                Sign in
                            </button>
                        </form>
                        <p className="mt-4 text-xs text-foreground/55">
                            Forgot your password? Please contact the developer to reset it.
                        </p>
                    </div>
                    <p className="mt-6 text-center text-xs text-foreground/55">
                        © {new Date().getFullYear()} Choice Ayurveda ·
                        <Link to="/" className="ml-1 hover:text-terracotta">
                            Back to public site
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
